import {
    PersistedClient,
    Persister,
    PersistQueryClientProvider,
} from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { createRoot } from 'react-dom/client';

import { App } from '/@/renderer/app';
import { queryClient } from '/@/renderer/lib/react-query';

function createIDBPersister(idbValidKey: IDBValidKey = 'reactQuery') {
    return {
        persistClient: async (client: PersistedClient) => {
            set(idbValidKey, client);
        },
        removeClient: async () => {
            await del(idbValidKey);
        },
        restoreClient: async () => {
            return await get<PersistedClient>(idbValidKey);
        },
    } as Persister;
}

const indexedDbPersister = createIDBPersister('feishin');

const CHUNK_RELOAD_KEY = 'feishin:chunk-reload-at';
const CHUNK_RELOAD_INTERVAL_MS = 60_000;

// After a deploy, a tab still running the previous build asks for chunks that
// no longer exist (the server answers 404). Reload once to pick up the new
// build instead of leaving a half-broken page; never more than once a minute,
// so a chunk that is really missing cannot cause a reload loop.
window.addEventListener('vite:preloadError', (event) => {
    try {
        const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
        if (Date.now() - last < CHUNK_RELOAD_INTERVAL_MS) return;
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
        return;
    }
    event.preventDefault();
    window.location.reload();
});

createRoot(document.getElementById('root')!).render(
    <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
            buster: 'feishin',
            dehydrateOptions: {
                // A mutation parked offline is an intent of this page and this
                // sign-in; it must not be replayed after a reload, possibly
                // under another login.
                shouldDehydrateMutation: () => false,
                shouldDehydrateQuery: (query) => {
                    const isSuccess = query.state.status === 'success';
                    const isLyricsQueryKey =
                        query.queryKey.includes('song') &&
                        query.queryKey.includes('lyrics') &&
                        query.queryKey.includes('select');

                    return isSuccess && isLyricsQueryKey;
                },
            },
            hydrateOptions: {
                defaultOptions: {
                    queries: {
                        gcTime: Infinity,
                    },
                },
            },
            maxAge: Infinity,
            persister: indexedDbPersister,
        }}
    >
        <App />
    </PersistQueryClientProvider>,
);
